/**
 * collection-calculator.js
 * wholesalemoulding.ca — Shopify Theme Calculator Engine
 *
 * Handles all 5 calculator types:
 *   - each         (simple qty)
 *   - box_based    (laminate/hardwood/LVP/tiles — sq ft → boxes)
 *   - linear_piece (16ft moulding — linear ft → pieces)
 *   - vinyl_sheet  (cut from roll — sq ft/yd → linear ft)
 *   - door         (qty + handing)
 */

(function () {
  'use strict';

  if (window._collectionCalculatorLoaded) {
    if (typeof window._collectionCalculatorInit === 'function') {
      window._collectionCalculatorInit();
    }
    return;
  }
  window._collectionCalculatorLoaded = true;

  /* ─────────────────────────────────────────
     UTILITY FUNCTIONS
  ───────────────────────────────────────── */

  /** Format cents (Shopify stores prices in cents) into $ string */
  function formatMoney(cents) {
    const amount = (cents / 100).toFixed(2);
    return '$' + parseFloat(amount).toLocaleString('en-CA', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    });
  }

  /** Round UP to nearest integer */
  function ceilTo(value, decimals) {
    if (decimals === undefined) decimals = 0;
    const factor = Math.pow(10, decimals);
    return Math.ceil(value * factor) / factor;
  }

  /** Show an element */
  function show(el) {
    if (el) el.style.display = '';
  }

  /** Hide an element */
  function hide(el) {
    if (el) el.style.display = 'none';
  }

  /** Set hidden input value only if changed */
  function setProp(el, value) {
    if (el && String(el.value) !== String(value)) {
      el.value = value;
    }
  }

  /* ─────────────────────────────────────────
     DYNAMIC SUBMIT BUTTON STATE CONTROLLER
  ───────────────────────────────────────── */
  // Inventory data comes from the separate Liquid JSON block.
  
  let calcCart = null;
  let calcCartRequest = null;
  const stockWrappers = new Set();

  function readCalcInventory(wrapper) {
    const node = wrapper.querySelector('[data-calc-inventory]');
    if (!node) return null;

    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      console.error('Calculator inventory JSON is invalid:', error);
      return null;
    }
  }

  function getCalcStockState(wrapper, submittedVariantId) {
    const inventory = readCalcInventory(wrapper);
    if (!inventory) {
      return { blocked: true, message: 'Inventory data unavailable' };
    }

    const selection = getCurrentVariantSelection(wrapper);
    const variantId = submittedVariantId ||
      (selection.variant && selection.variant.id);

    if (!variantId || !inventory[String(variantId)]) {
      return { blocked: true, message: 'Please select an option' };
    }

    const stock = inventory[String(variantId)];

    // No inventory restriction for untracked variants.
    if (!stock.tracked) {
      return { blocked: false, tracked: false };
    }

    if (!calcCart) {
      return {
        blocked: true,
        tracked: true,
        message: 'Checking availability'
      };
    }

    const stockQuantity = Number(stock.quantity);
    if (!Number.isFinite(stockQuantity)) {
      return {
        blocked: true,
        tracked: true,
        message: 'Inventory data unavailable'
      };
    }

    const inCart = calcCart.items.reduce(function (total, item) {
      return String(item.variant_id) === String(variantId)
        ? total + Number(item.quantity)
        : total;
    }, 0);

    // Use the calculator's actual cart units, not boxes or pieces.
    const qtyInput =
      wrapper.querySelector('input[name="quantity"]') ||
      wrapper.querySelector('[data-calc-qty]');

    const requested = Number(qtyInput ? qtyInput.value : 0);
    const remaining = Math.max(0, stockQuantity - inCart);
    const blocked = requested > remaining || remaining === 0;

    return {
      blocked: blocked,
      tracked: true,
      remaining: remaining,
      message: remaining === 0
        ? 'No more stock available to add'
        : 'Quantity exceeds available stock'
    };
  }

  function applyCalcStockState(wrapper) {
    const button = getSubmitButton(wrapper);
    if (!button) return;

    const stock = getCalcStockState(wrapper);
    let message = wrapper.querySelector('[data-calc-stock-message]');

    if (!message) {
      message = document.createElement('p');
      message.setAttribute('data-calc-stock-message', '');
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      wrapper.appendChild(message);
    }

    message.textContent = stock.blocked ? stock.message : '';
    message.hidden = !stock.blocked;

    if (stock.blocked) {
      button.disabled = true;
      button.setAttribute('aria-disabled', 'true');
    }
  }

  function redrawCalcStockButtons() {
    stockWrappers.forEach(function (wrapper) {
      if (!wrapper.isConnected) {
        stockWrappers.delete(wrapper);
        return;
      }

      const args = wrapper._calcButtonArgs;
      if (args) {
        setButtonState.apply(null, args);
      }
    });
  }

  function refreshCalcCart() {
    if (calcCartRequest) return calcCartRequest;

    const root = window.Shopify && window.Shopify.routes
      ? window.Shopify.routes.root
      : '/';

    calcCartRequest = fetch(root + 'cart.js', {
      credentials: 'same-origin',
      cache: 'no-store'
    })
      .then(function (response) {
        if (!response.ok) throw new Error('Could not read cart');
        return response.json();
      })
      .then(function (cart) {
        if (!Array.isArray(cart.items)) {
          throw new Error('Invalid cart response');
        }
        calcCart = cart;
        return cart;
      })
      .finally(function () {
        calcCartRequest = null;
        redrawCalcStockButtons();
      });

    return calcCartRequest;
  }

  // Refresh after cart changes, including changes in the cart drawer.
  // The fresh submit check below does not rely on this timer.
  window.setInterval(function () {
    if (document.hidden || !stockWrappers.size) return;
    refreshCalcCart().catch(function (error) {
      console.warn(error);
    });
  }, 3000);

  window.addEventListener('pageshow', function () {
    if (stockWrappers.size) {
      refreshCalcCart().catch(console.warn);
    }
  });

  function getSubmitButton(wrapper) {
    const sectionId = wrapper.dataset.sectionId;
    return (
      document.getElementById('ProductSubmitButton-' + sectionId) ||
      document.querySelector('.product-form__submit') ||
      document.querySelector('[name="add"]')
    );
  }

    function setButtonState(wrapper, isReady, label, totalCents, primaryInput) {
    wrapper._calcButtonArgs = [
      wrapper, isReady, label, totalCents, primaryInput
    ];

    const btn = getSubmitButton(wrapper);
    if (!btn) return;

    let span = btn.querySelector('span');
    if (!span) span = btn;

    if (!isReady) {
      btn.setAttribute('data-calc-ready', 'false');
      btn.classList.add('product-form__submit--pending');
      span.textContent = label;
      btn.setAttribute('aria-disabled', 'true');
    } else {
      btn.setAttribute('data-calc-ready', 'true');
      btn.classList.remove('product-form__submit--pending');
      btn.removeAttribute('aria-disabled');
      btn.removeAttribute('disabled');

      if (totalCents && totalCents > 0) {
        span.textContent = 'ADD TO CART — ' + formatMoney(totalCents);
      } else {
        span.textContent = label || 'ADD TO CART';
      }
    }

    // Keep the current input reference for the click handler.
    btn._calcPrimaryInput = primaryInput;

    if (!btn._pendingBound) {
      btn._pendingBound = true;

      btn.addEventListener('click', function (e) {
        if (btn.getAttribute('data-calc-ready') === 'false') {
          e.preventDefault();
          e.stopImmediatePropagation();

          const targetInp = btn._calcPrimaryInput ||
            wrapper.querySelector('input[type="number"]:not([type="hidden"])');

          if (targetInp) {
            targetInp.classList.remove('calc__input--flash');
            void targetInp.offsetWidth;
            targetInp.classList.add('calc__input--flash');
            targetInp.scrollIntoView({
              behavior: 'smooth',
              block: 'center'
            });
            targetInp.focus();

            setTimeout(function () {
              targetInp.classList.remove('calc__input--flash');
            }, 1200);
          }

          showCalcToast(
            'Please enter your measurements above to calculate pricing.'
          );
        }
      }, true);
    }

    // Always check stock AFTER the calculator updates the button.
    applyCalcStockState(wrapper);
  }


  /* ─────────────────────────────────────────
     CENTRALIZED VARIANT RESOLVER & EVENT DISPATCHER
  ───────────────────────────────────────── */

  /** Helper: Read embedded variants JSON from the calculator wrapper */
  function getVariantsData(wrapper) {
    if (wrapper._calcVariants) return wrapper._calcVariants;
    const script = wrapper.querySelector('[data-calc-variants]');
    if (script && script.textContent) {
      try {
        wrapper._calcVariants = JSON.parse(script.textContent);
        return wrapper._calcVariants;
      } catch (e) {
        console.warn('Could not parse [data-calc-variants]:', e);
      }
    }
    return [];
  }

  /**
   * Helper: Find currently selected variant and options from DOM
   * Inspects checked radios, dropdown selects, form inputs, and variant JSON.
   */
  function getCurrentVariantSelection(wrapper) {
    const sectionId = wrapper.dataset.sectionId;
    const root = wrapper.closest('product-info') ||
                 document.getElementById('MainProduct-' + sectionId) ||
                 document.querySelector('product-info') ||
                 document;

    const selectedValues = [];
    const selectedOptionsMap = {};

    // 1. Collect checked radio buttons (pills, swatches)
    const checkedInputs = root.querySelectorAll(
      'variant-selects input[type="radio"]:checked, .product-form__input input[type="radio"]:checked, variant-radios input[type="radio"]:checked'
    );
    checkedInputs.forEach(function (input) {
      const val = (input.value || '').trim();
      if (val) {
        selectedValues.push(val);
        const name = (input.name || '').toLowerCase().replace(/-\d+$/, '').trim();
        if (name) selectedOptionsMap[name] = val;
      }
    });

    // 2. Collect select dropdowns
    const selects = root.querySelectorAll(
      'variant-selects select, .product-form__input select, variant-radios select'
    );
    selects.forEach(function (select) {
      const val = (select.value || '').trim();
      if (val) {
        selectedValues.push(val);
        const name = (select.name || '').toLowerCase().replace(/^options\[|\]$/g, '').trim();
        if (name) selectedOptionsMap[name] = val;
      }
    });

    // 3. Form input[name="id"]
    const formId = 'product-form-' + sectionId;
    const form = document.getElementById(formId) ||
                 root.querySelector('product-form form') ||
                 document.querySelector('form[action*="/cart/add"]');
    const idInput = form ? form.querySelector('input[name="id"]') : document.querySelector('input[name="id"]');
    const formVariantId = idInput && idInput.value ? parseInt(idInput.value, 10) : null;

    // 4. Match against variants array
    const variants = getVariantsData(wrapper);
    let matchedVariant = null;

        if (variants && variants.length > 0) {
      if (selectedValues.length > 0) {
        // Match every selected option in its actual order.
        matchedVariant = variants.find(function (v) {
          const options = [v.option1, v.option2, v.option3]
            .filter(function (option) {
              return option !== null && option !== undefined;
            })
            .map(function (option) {
              return String(option).trim();
            });

          return options.length === selectedValues.length &&
            options.every(function (option, index) {
              return option === selectedValues[index];
            });
        });
      } else if (formVariantId) {
        matchedVariant = variants.find(function (v) {
          return String(v.id) === String(formVariantId);
        });
      } else if (wrapper.dataset.variantId) {
        matchedVariant = variants.find(function (v) {
          return String(v.id) === String(wrapper.dataset.variantId);
        });
      }
    }


    // Determine the length string
    let lengthStr = '';
    const lengthKey = Object.keys(selectedOptionsMap).find(function (k) {
      return k.includes('length') || k.includes('size');
    });
    if (lengthKey && selectedOptionsMap[lengthKey]) {
      lengthStr = selectedOptionsMap[lengthKey];
    } else {
      const found = selectedValues.find(function (v) {
        const s = v.toLowerCase();
        return s.includes('"') || s.includes("'") || s.includes('ft') || /^\d+/.test(s);
      });
      if (found) lengthStr = found;
    }

    if (!lengthStr && matchedVariant) {
      lengthStr = matchedVariant.option1 || matchedVariant.title;
    }

    return {
      variant: matchedVariant,
      formVariantId: formVariantId,
      idInput: idInput,
      selectedValues: selectedValues,
      lengthStr: lengthStr,
      variants: variants
    };
  }

  // Safe global registry of callbacks
  const syncCallbacks = [];
  let syncDebounceTimer = null;

  function triggerGlobalVariantSync(explicitVariant) {
    clearTimeout(syncDebounceTimer);
    syncDebounceTimer = setTimeout(function () {
      for (let i = 0; i < syncCallbacks.length; i++) {
        try {
          syncCallbacks[i](explicitVariant);
        } catch (e) {
          console.error('Error in variant sync:', e);
        }
      }
    }, 30);
  }

  function registerVariantListener(callback) {
    syncCallbacks.push(callback);

    if (window._calcGlobalVariantListenersBound) return;
    window._calcGlobalVariantListenersBound = true;

    // Delegated change on document (catches variant radios & selects)
    document.addEventListener('change', function (e) {
      const target = e.target;
      if (!target) return;
      if (
        target.closest('variant-selects') ||
        target.closest('variant-radios') ||
        target.closest('.product-form__input') ||
        target.name === 'id'
      ) {
        triggerGlobalVariantSync();
      }
    }, true);

    // Delegated click on document (catches pill labels & radio buttons)
    document.addEventListener('click', function (e) {
      const target = e.target;
      if (!target) return;
      if (
        target.closest('.product-form__input label') ||
        target.closest('variant-selects label') ||
        target.closest('variant-radios label') ||
        (target.tagName === 'INPUT' && target.type === 'radio' && target.closest('.product-form__input, variant-selects, variant-radios'))
      ) {
        triggerGlobalVariantSync();
      }
    }, true);

    // PubSub events
    function tryPubSub() {
      if (window.subscribe && window.PUB_SUB_EVENTS) {
        if (window.PUB_SUB_EVENTS.variantChange) {
          window.subscribe(window.PUB_SUB_EVENTS.variantChange, function (event) {
            triggerGlobalVariantSync(event && event.data ? event.data.variant : null);
          });
        }
        if (window.PUB_SUB_EVENTS.optionValueSelectionChange) {
          window.subscribe(window.PUB_SUB_EVENTS.optionValueSelectionChange, function () {
            triggerGlobalVariantSync();
          });
        }
        return true;
      }
      return false;
    }

    if (!tryPubSub()) {
      window.addEventListener('load', tryPubSub, { once: true });
    }
  }

  /* ─────────────────────────────────────────
     EACH CALCULATOR
  ───────────────────────────────────────── */

  function initCalcEach(wrapper) {
    let basePrice = parseInt(wrapper.dataset.basePrice, 10) || 0;
    const qtyInput = wrapper.querySelector('[data-calc-qty]');
    const totalEl = wrapper.querySelector('[data-calc-total]');
    const unitPriceEl = wrapper.querySelector('[data-calc-unit-price]');
    const minusBtn = wrapper.querySelector('[data-action="minus"]');
    const plusBtn = wrapper.querySelector('[data-action="plus"]');

    function update() {
      const qty = Math.max(1, parseInt(qtyInput.value, 10) || 1);
      qtyInput.value = qty;
      const totalCents = basePrice * qty;
      if (unitPriceEl) unitPriceEl.textContent = formatMoney(basePrice);
      if (totalEl) totalEl.textContent = formatMoney(totalCents);
      setButtonState(wrapper, true, 'ADD TO CART', totalCents, null);
    }

    function syncVariant(explicitVariant) {
      let newPrice = null;
      if (explicitVariant && explicitVariant.price) {
        newPrice = explicitVariant.price;
      } else {
        const selection = getCurrentVariantSelection(wrapper);
        if (selection && selection.variant && selection.variant.price) {
          newPrice = selection.variant.price;
        }
      }
      if (newPrice !== null && newPrice !== basePrice) {
        basePrice = newPrice;
        update();
      }
    }

    if (qtyInput) qtyInput.addEventListener('input', update);
    if (minusBtn && !minusBtn._calcBound) {
      minusBtn._calcBound = true;
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
        update();
      });
    }
    if (plusBtn && !plusBtn._calcBound) {
      plusBtn._calcBound = true;
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
        update();
      });
    }

    registerVariantListener(syncVariant);
    update();
  }

  /* ─────────────────────────────────────────
     BOX-BASED CALCULATOR
  ───────────────────────────────────────── */

  function initCalcBoxBased(wrapper) {
    let basePrice = parseInt(wrapper.dataset.basePrice, 10) || 0;
    let coveragePerBox = parseFloat(wrapper.dataset.coverage) || 0;
    const hasMetafield = wrapper.dataset.hasMetafield === 'true';
    const productTitle = wrapper.dataset.productTitle || '';

    // Smart auto-detection from product title if metafield is not explicitly populated
    if (!hasMetafield && productTitle) {
      const match = productTitle.match(/(\d+(?:\.\d+)?)\s*(?:sq\s*ft|sqft|sf)\b/i);
      if (match && match[1]) {
        const titleCoverage = parseFloat(match[1]);
        if (titleCoverage > 0) {
          coveragePerBox = titleCoverage;
          const badgeVal = wrapper.querySelector('[data-calc-coverage-val]');
          if (badgeVal) badgeVal.textContent = coveragePerBox + ' sq ft';
        }
      }
    }

    const areaInput = wrapper.querySelector('[data-calc-area]');
    const wasteSelect = wrapper.querySelector('[data-calc-waste]');
    const summaryEl = wrapper.querySelector('[id^="calc-summary-"]');
    const boxesEl = wrapper.querySelector('[data-calc-boxes]');
    const areaWasteEl = wrapper.querySelector('[data-calc-area-waste]');
    const boxCoverageEl = wrapper.querySelector('[data-calc-box-coverage]');
    const unitPriceEl = wrapper.querySelector('[data-calc-unit-price]');
    const totalEl = wrapper.querySelector('[data-calc-total]');

    // Hidden props
    const qtyInput = wrapper.querySelector('[data-calc-qty]');
    const propArea = wrapper.querySelector('[data-prop-area]');
    const propWaste = wrapper.querySelector('[data-prop-waste]');
    const propBoxes = wrapper.querySelector('[data-prop-boxes]');
    const propCoverage = wrapper.querySelector('[data-prop-coverage]');
    const propCartQtyLabel = wrapper.querySelector('[data-prop-cart-qty-label]');

    function update() {
      const area = parseFloat(areaInput.value) || 0;
      const wastePercent = parseInt(wasteSelect.value, 10) || 0;

      if (area <= 0 || coveragePerBox <= 0) {
        hide(summaryEl);
        setProp(qtyInput, '');
        setButtonState(wrapper, false, 'ENTER ROOM AREA TO ADD TO CART', 0, areaInput);
        return;
      }

      const areaWithWaste = area * (1 + wastePercent / 100);
      const boxesNeeded = Math.ceil(areaWithWaste / coveragePerBox);
      const totalCoverage = boxesNeeded * coveragePerBox;
      const cartQty = Math.round(totalCoverage);
      const totalCents = Math.round(cartQty * basePrice);

      show(summaryEl);
      if (areaWasteEl) areaWasteEl.textContent = areaWithWaste.toFixed(1) + ' sq ft';
      if (boxesEl) boxesEl.textContent = boxesNeeded + ' box' + (boxesNeeded !== 1 ? 'es' : '');
      if (boxCoverageEl) boxCoverageEl.textContent = totalCoverage.toFixed(1) + ' sq ft';
      if (unitPriceEl) unitPriceEl.textContent = formatMoney(basePrice) + '/sq ft';
      if (totalEl) totalEl.textContent = formatMoney(totalCents);

      const boxesLabel = boxesNeeded + ' box' + (boxesNeeded !== 1 ? 'es' : '');

      setProp(qtyInput, cartQty);
      setProp(propArea, area + ' sq ft');
      setProp(propWaste, wastePercent + '%');
      setProp(propBoxes, boxesLabel);
      setProp(propCoverage, totalCoverage.toFixed(1) + ' sq ft');
      setProp(propCartQtyLabel, boxesLabel);

      setButtonState(wrapper, true, 'ADD TO CART', totalCents, areaInput);
    }

    function syncVariant(explicitVariant) {
      let newPrice = null;
      if (explicitVariant && explicitVariant.price) {
        newPrice = explicitVariant.price;
      } else {
        const selection = getCurrentVariantSelection(wrapper);
        if (selection && selection.variant && selection.variant.price) {
          newPrice = selection.variant.price;
        }
      }
      if (newPrice !== null && newPrice !== basePrice) {
        basePrice = newPrice;
        update();
      }
    }
    if (areaInput) areaInput.addEventListener('input', update);
    if (wasteSelect) wasteSelect.addEventListener('change', update);

    registerVariantListener(syncVariant);
    update();
  }

  /* ─────────────────────────────────────────
     MOULDING / PIECE CALCULATOR
     - Short pieces (84", 96", etc.): sold per piece (1x multiplier)
     - Long pieces (144" = 12x, 168" = 14x, 192" = 16x linear ft multiplier)
  ───────────────────────────────────────── */

  function getMouldingConfig(variantTitleOrOption) {
    if (!variantTitleOrOption) return { multiplier: 1, isLinear: false, lengthLabel: 'Standard' };
    const str = String(variantTitleOrOption).toLowerCase();

    if (str.includes('192') || str.includes("16'") || str.includes('16 ft') || str.includes('16ft') || str.includes('16-foot')) {
      return { multiplier: 16, isLinear: true, lengthLabel: '192" (16 ft)' };
    }
    if (str.includes('168') || str.includes("14'") || str.includes('14 ft') || str.includes('14ft') || str.includes('14-foot')) {
      return { multiplier: 14, isLinear: true, lengthLabel: '168" (14 ft)' };
    }
    if (str.includes('144') || str.includes("12'") || str.includes('12 ft') || str.includes('12ft') || str.includes('12-foot')) {
      return { multiplier: 12, isLinear: true, lengthLabel: '144" (12 ft)' };
    }

    const inchMatch = str.match(/(\d{3})\s*"/);
    if (inchMatch) {
      const inches = parseInt(inchMatch[1], 10);
      if (inches >= 144 && inches % 12 === 0) {
        const feet = inches / 12;
        return { multiplier: feet, isLinear: true, lengthLabel: inches + '" (' + feet + ' ft)' };
      }
    }

    const shortMatch = str.match(/(\d+)\s*"?/);
    const label = shortMatch ? shortMatch[1] + '"' : (variantTitleOrOption || 'Standard');
    return { multiplier: 1, isLinear: false, lengthLabel: label };
  }

  function initCalcMoulding(wrapper) {
    let basePrice = parseInt(wrapper.dataset.basePrice, 10) || 0;

    const piecesInput = wrapper.querySelector('[data-calc-moulding-pieces]') || wrapper.querySelector('[data-calc-qty]');
    const minusBtn = wrapper.querySelector('[data-action="minus"]');
    const plusBtn = wrapper.querySelector('[data-action="plus"]');

    const lengthLabelEl = wrapper.querySelector('[data-calc-length-label]');
    const tagEl = wrapper.querySelector('[data-calc-moulding-tag]');
    const rateRowEl = wrapper.querySelector('[id^="calc-linear-rate-row-"]');
    const rateValEl = wrapper.querySelector('[data-calc-rate]');
    const pieceLabelEl = wrapper.querySelector('[data-calc-piece-label]');
    const piecePriceEl = wrapper.querySelector('[data-calc-piece-price]');
    const totalEl = wrapper.querySelector('[data-calc-total]');

    // Hidden inputs
    const hiddenQty = wrapper.querySelector('input[name="quantity"]') || wrapper.querySelector('[data-calc-qty]');
    const propLength = wrapper.querySelector('[data-prop-length]');
    const propPieces = wrapper.querySelector('[data-prop-pieces]');
    const propLinearFt = wrapper.querySelector('[data-prop-linear-ft]');

    let config = getMouldingConfig(lengthLabelEl ? lengthLabelEl.textContent.trim() : '');
    let lastVariantId = null;
    let lastLengthStr = '';

    function update() {
      const qty = Math.max(1, parseInt(piecesInput ? piecesInput.value : 1, 10) || 1);
      if (piecesInput) piecesInput.value = qty;

      const piecePrice = basePrice * config.multiplier;
      const totalCents = qty * piecePrice;
      const cartQty = qty * config.multiplier;

      if (lengthLabelEl) lengthLabelEl.textContent = config.lengthLabel;
      if (tagEl) {
        tagEl.textContent = config.isLinear ? 'Sold as full ' + config.multiplier + '-ft piece' : 'Sold per piece';
        if (config.isLinear) {
          tagEl.classList.remove('calc__tag--piece');
        } else {
          tagEl.classList.add('calc__tag--piece');
        }
      }
      if (rateRowEl) {
        rateRowEl.style.display = config.isLinear ? '' : 'none';
      }
      if (rateValEl) rateValEl.textContent = formatMoney(basePrice) + '/linear ft';
      if (pieceLabelEl) pieceLabelEl.textContent = config.isLinear ? 'Full Piece Price (' + config.multiplier + ' ft)' : 'Unit Price';
      if (piecePriceEl) piecePriceEl.textContent = formatMoney(piecePrice);
      if (totalEl) totalEl.textContent = formatMoney(totalCents);

      setProp(hiddenQty, cartQty);
      setProp(propLength, config.lengthLabel);
      setProp(propPieces, qty + ' piece' + (qty !== 1 ? 's' : ''));
      setProp(propLinearFt, config.isLinear ? cartQty + ' linear ft' : 'N/A');

      setButtonState(wrapper, true, 'ADD TO CART', totalCents, null);
    }

    function syncVariant(explicitVariant) {
      if (explicitVariant && explicitVariant.price) {
        if (lastVariantId === explicitVariant.id) return;
        lastVariantId = explicitVariant.id;

        basePrice = explicitVariant.price;
        let lStr = explicitVariant.option1 || explicitVariant.title;
        if (explicitVariant.options && explicitVariant.options.length) {
          lStr = explicitVariant.options.join(' ');
        }
        lastLengthStr = lStr;
        config = getMouldingConfig(lStr);
        update();
        return;
      }

      const selection = getCurrentVariantSelection(wrapper);
      if (selection && selection.variant) {
        if (selection.variant.id === lastVariantId && selection.lengthStr === lastLengthStr) {
          return;
        }
        lastVariantId = selection.variant.id;
        lastLengthStr = selection.lengthStr;

        basePrice = selection.variant.price;
        config = getMouldingConfig(selection.lengthStr || selection.variant.title);

        if (selection.idInput && String(selection.idInput.value) !== String(selection.variant.id)) {
          selection.idInput.value = selection.variant.id;
        }
        update();
      } else if (selection && selection.lengthStr && selection.lengthStr !== lastLengthStr) {
        lastLengthStr = selection.lengthStr;
        config = getMouldingConfig(selection.lengthStr);
        update();
      }
    }

    if (piecesInput) piecesInput.addEventListener('input', update);
    if (minusBtn && !minusBtn._calcBound) {
      minusBtn._calcBound = true;
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (piecesInput) {
          piecesInput.value = Math.max(1, (parseInt(piecesInput.value, 10) || 1) - 1);
          update();
        }
      });
    }
    if (plusBtn && !plusBtn._calcBound) {
      plusBtn._calcBound = true;
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (piecesInput) {
          piecesInput.value = (parseInt(piecesInput.value, 10) || 1) + 1;
          update();
        }
      });
    }

    registerVariantListener(syncVariant);
    syncVariant();
  }

  /* ─────────────────────────────────────────
     VINYL SHEET CALCULATOR
     - Price entered in Shopify is per sq ft
     - Total = Total Area (sq ft) × Price per sq ft
     - Cart quantity = Total Area (sq ft)
  ───────────────────────────────────────── */

  function initCalcVinylSheet(wrapper) {
    let basePrice = parseInt(wrapper.dataset.basePrice, 10) || 0;
    const rollWidth = parseInt(wrapper.dataset.rollWidth, 10) || 12;

    const uomSelect = wrapper.querySelector('[data-calc-uom]');
    const lengthFtInput = wrapper.querySelector('[data-calc-length-ft]');
    const lengthInSelect = wrapper.querySelector('[data-calc-length-in]');
    const dyelotCheck = wrapper.querySelector('[data-calc-dyelot]');
    const summaryEl = wrapper.querySelector('[id^="calc-summary-"]');
    const areaDisplayEl = wrapper.querySelector('[data-calc-area-display]');
    const linearFtEl = wrapper.querySelector('[data-calc-linear-ft-display]');
    const unitPriceEl = wrapper.querySelector('[data-calc-unit-price]');
    const totalEl = wrapper.querySelector('[data-calc-total]');

    // Hidden prop inputs
    const qtyInput = wrapper.querySelector('[data-calc-qty]');
    const propUom = wrapper.querySelector('[data-prop-uom]');
    const propArea = wrapper.querySelector('[data-prop-area]');
    const propLinearFt = wrapper.querySelector('[data-prop-linear-ft]');
    const propDyelot = wrapper.querySelector('[data-prop-dyelot]');
    const propCartQtyLabel = wrapper.querySelector('[data-prop-cart-qty-label]');

    const lengthLabelEl = wrapper.querySelector('[data-calc-length-label]');
    const subFieldEl = wrapper.querySelector('[data-calc-sub-field]');
    const subLabelEl = wrapper.querySelector('[data-calc-sub-label]');

    function syncUomUI() {
      const uom = uomSelect ? uomSelect.value : 'linear_ft';
      if (uom === 'linear_m') {
        if (lengthLabelEl) lengthLabelEl.textContent = 'Length — Meters';
        if (lengthFtInput) lengthFtInput.placeholder = 'e.g. 5';
        if (subFieldEl) subFieldEl.style.display = '';
        if (subLabelEl) subLabelEl.textContent = 'Centimeters (approx)';
      } else if (uom === 'sqyd') {
        if (lengthLabelEl) lengthLabelEl.textContent = 'Area — Square Yards';
        if (lengthFtInput) lengthFtInput.placeholder = 'e.g. 20';
        if (subFieldEl) subFieldEl.style.display = 'none';
      } else if (uom === 'sqm') {
        if (lengthLabelEl) lengthLabelEl.textContent = 'Area — Square Meters';
        if (lengthFtInput) lengthFtInput.placeholder = 'e.g. 25';
        if (subFieldEl) subFieldEl.style.display = 'none';
      } else if (uom === 'sqft') {
        if (lengthLabelEl) lengthLabelEl.textContent = 'Area — Square Feet';
        if (lengthFtInput) lengthFtInput.placeholder = 'e.g. 150';
        if (subFieldEl) subFieldEl.style.display = 'none';
      } else {
        // Default: linear_ft
        if (lengthLabelEl) lengthLabelEl.textContent = 'Length — Feet';
        if (lengthFtInput) lengthFtInput.placeholder = 'e.g. 14';
        if (subFieldEl) subFieldEl.style.display = '';
        if (subLabelEl) subLabelEl.textContent = 'Inches';
      }
    }

    function update() {
      const uom = uomSelect ? uomSelect.value : 'linear_ft';
      syncUomUI();

      const lengthFt = parseFloat(lengthFtInput ? lengthFtInput.value : 0) || 0;
      const lengthIn = parseInt(lengthInSelect ? lengthInSelect.value : 0, 10) || 0;

      if (lengthFt <= 0) {
        hide(summaryEl);
        setProp(qtyInput, '');
        setButtonState(wrapper, false, 'ENTER LENGTH TO ADD TO CART', 0, lengthFtInput);
        return;
      }

      let totalLengthFt = 0;
      let areaSqFt = 0;
      let areaLabel = '';
      let uomDisplayName = '';

      if (uom === 'linear_m') {
        const totalLengthM = lengthFt + (lengthIn / 100);
        totalLengthFt = totalLengthM * 3.28084;
        areaSqFt = totalLengthFt * rollWidth;
        areaLabel = (areaSqFt / 10.7639).toFixed(2) + ' sq m (' + areaSqFt.toFixed(2) + ' sq ft)';
        uomDisplayName = 'Linear Meters';
      } else if (uom === 'sqyd') {
        const areaSqYd = lengthFt;
        areaSqFt = areaSqYd * 9;
        totalLengthFt = areaSqFt / rollWidth;
        areaLabel = areaSqYd.toFixed(2) + ' sq yd (' + areaSqFt.toFixed(2) + ' sq ft)';
        uomDisplayName = 'Square Yards';
      } else if (uom === 'sqm') {
        const areaSqM = lengthFt;
        areaSqFt = areaSqM * 10.7639;
        totalLengthFt = areaSqFt / rollWidth;
        areaLabel = areaSqM.toFixed(2) + ' sq m (' + areaSqFt.toFixed(2) + ' sq ft)';
        uomDisplayName = 'Square Meters';
      } else if (uom === 'sqft') {
        areaSqFt = lengthFt;
        totalLengthFt = areaSqFt / rollWidth;
        areaLabel = areaSqFt.toFixed(2) + ' sq ft';
        uomDisplayName = 'Square Feet';
      } else {
        // Default: linear_ft
        totalLengthFt = lengthFt + (lengthIn / 12);
        areaSqFt = totalLengthFt * rollWidth;
        areaLabel = areaSqFt.toFixed(2) + ' sq ft';
        uomDisplayName = 'Linear Feet';
      }

      // Exact length as ordered — never rounded up to the next foot
      const linearFtLabel = totalLengthFt.toFixed(2).replace(/\.00$/, '') + ' linear ft';
      const cartQty = Math.max(1, Math.round(areaSqFt));
      const totalCents = Math.round(cartQty * basePrice);

      show(summaryEl);
      if (areaDisplayEl) areaDisplayEl.textContent = areaLabel;
      if (linearFtEl) linearFtEl.textContent = linearFtLabel + ' from ' + rollWidth + 'ft wide roll';
      if (unitPriceEl) unitPriceEl.textContent = formatMoney(basePrice) + '/sq ft';
      if (totalEl) totalEl.textContent = formatMoney(totalCents);

      setProp(qtyInput, cartQty);
      setProp(propUom, uomDisplayName);
      setProp(propArea, areaLabel);
      setProp(propLinearFt, linearFtLabel);
      setProp(propDyelot, dyelotCheck && dyelotCheck.checked ? 'Yes' : 'No');
      setProp(propCartQtyLabel, '1 Piece');

      setButtonState(wrapper, true, 'ADD TO CART', totalCents, lengthFtInput);
    }

    function syncVariant(explicitVariant) {
      let newPrice = null;
      if (explicitVariant && explicitVariant.price) {
        newPrice = explicitVariant.price;
      } else {
        const selection = getCurrentVariantSelection(wrapper);
        if (selection && selection.variant && selection.variant.price) {
          newPrice = selection.variant.price;
        }
      }
      if (newPrice !== null && newPrice !== basePrice) {
        basePrice = newPrice;
        update();
      }
    }

    const inputs = [uomSelect, lengthFtInput, lengthInSelect, dyelotCheck];
    inputs.forEach(function (el) {
      if (el) el.addEventListener('change', update);
      if (el && el.tagName === 'INPUT' && el.type !== 'checkbox') {
        el.addEventListener('input', update);
      }
    });

    registerVariantListener(syncVariant);
    update();
  }

  /* ─────────────────────────────────────────
     DOOR CALCULATOR
     - Exterior doors: Single door only (no double door, no handing)
     - Interior doors: Single or Double with Handing
  ───────────────────────────────────────── */

  function initCalcDoor(wrapper) {
    let basePrice = parseInt(wrapper.dataset.basePrice, 10) || 0;
    const isExterior = wrapper.dataset.isExterior === 'true';

    const qtyInput = wrapper.querySelector('[data-calc-door-qty]');
    const unitPriceEl = wrapper.querySelector('[data-calc-unit-price]');
    const totalEl = wrapper.querySelector('[data-calc-total]');
    const minusBtn = wrapper.querySelector('[data-action="minus"]');
    const plusBtn = wrapper.querySelector('[data-action="plus"]');

    const hiddenQty = wrapper.querySelector('[data-calc-qty]');

    /** Safely parse any extra add-on fee selected in Ymq Product Options */
    function getYmqAddonCents() {
      let addonCents = 0;
      // 1. Inspect all select dropdowns from Ymq or custom option containers
      const selects = document.querySelectorAll(
        '.ymq-options select, [class*="ymq"] select, form[action*="/cart/add"] select:not([name="id"]):not([id^="calc-"])'
      );
      selects.forEach(function (sel) {
        if (sel.closest('variant-selects') || sel.closest('variant-radios')) return;
        const opt = sel.options[sel.selectedIndex];
        if (opt && opt.text) {
          const match = opt.text.match(/\+\s*(?:CAD|\$)?\s*([\d,]+(?:\.\d{1,2})?)\s*(?:CAD|\$)?/i);
          if (match) {
            const val = parseFloat(match[1].replace(/,/g, ''));
            if (!isNaN(val)) {
              addonCents += Math.round(val * 100);
            }
          }
        }
      });

      // 2. Inspect checked radio buttons or checkboxes from Ymq
      const checkedInputs = document.querySelectorAll(
        '.ymq-options input:checked, [class*="ymq"] input:checked'
      );
      checkedInputs.forEach(function (inp) {
        const label = inp.closest('label') || document.querySelector('label[for="' + CSS.escape(inp.id) + '"]');
        const text = label ? label.textContent : '';
        const match = text.match(/\+\s*(?:CAD|\$)?\s*([\d,]+(?:\.\d{1,2})?)\s*(?:CAD|\$)?/i);
        if (match) {
          const val = parseFloat(match[1].replace(/,/g, ''));
          if (!isNaN(val)) {
            addonCents += Math.round(val * 100);
          }
        }
      });

      return addonCents;
    }

    /** Detect if Double Door / Pair is selected in Ymq or custom options */
    function isDoubleDoorSelected() {
      // 1. Inspect select dropdowns
      const selects = document.querySelectorAll(
        '.ymq-options select, [class*="ymq"] select, form[action*="/cart/add"] select:not([name="id"]):not([id^="calc-"])'
      );
      for (let i = 0; i < selects.length; i++) {
        const sel = selects[i];
        if (sel.closest('variant-selects') || sel.closest('variant-radios')) continue;
        const opt = sel.options[sel.selectedIndex];
        if (opt && opt.text) {
          const txt = opt.text.toLowerCase();
          if (txt.includes('double') || txt.includes('pair')) {
            return true;
          }
        }
      }

      // 2. Inspect checked radio buttons or checkboxes
      const checkedInputs = document.querySelectorAll(
        '.ymq-options input:checked, [class*="ymq"] input:checked'
      );
      for (let i = 0; i < checkedInputs.length; i++) {
        const inp = checkedInputs[i];
        const label = inp.closest('label') || document.querySelector('label[for="' + CSS.escape(inp.id) + '"]');
        const txt = ((label ? label.textContent : '') + ' ' + (inp.value || '')).toLowerCase();
        if (txt.includes('double') || txt.includes('pair')) {
          return true;
        }
      }

      return false;
    }

    function getDoorSelectedOptions() {
      let doorConfig = 'Slab Only';
      let handing = 'N/A';

      // 1. Inspect select dropdowns
      const selects = document.querySelectorAll(
        '.ymq-options select, [class*="ymq"] select, form[action*="/cart/add"] select:not([name="id"]):not([id^="calc-"])'
      );
      selects.forEach(function (sel) {
        if (sel.closest('variant-selects') || sel.closest('variant-radios')) return;
        const opt = sel.options[sel.selectedIndex];
        if (!opt || !opt.text) return;
        const txt = opt.text.toLowerCase();
        if (txt.includes('prehung')) {
          doorConfig = 'Prehung';
        } else if (txt.includes('slab')) {
          doorConfig = 'Slab Only';
        }

        if (txt.includes('double') || txt.includes('pair')) {
          handing = 'Double Door (Pair)';
        } else if (txt.includes('lhis') || txt.includes('rhos') || txt.includes('rhis') || txt.includes('lhos')) {
          handing = opt.text.replace(/\s*\(\+.*?\)/, '').trim();
        } else if (txt.includes('left hand') || txt.includes('right hand')) {
          handing = opt.text.replace(/\s*\(\+.*?\)/, '').trim();
        }
      });

      // 2. Inspect checked radio buttons or checkboxes
      const checkedInputs = document.querySelectorAll(
        '.ymq-options input:checked, [class*="ymq"] input:checked'
      );
      checkedInputs.forEach(function (inp) {
        const label = inp.closest('label') || document.querySelector('label[for="' + CSS.escape(inp.id) + '"]');
        const txt = ((label ? label.textContent : '') + ' ' + (inp.value || '')).toLowerCase();
        if (txt.includes('prehung')) {
          doorConfig = 'Prehung';
        } else if (txt.includes('slab')) {
          doorConfig = 'Slab Only';
        }

        if (txt.includes('double') || txt.includes('pair')) {
          handing = 'Double Door (Pair)';
        } else if (txt.includes('lhis') || txt.includes('rhos') || txt.includes('rhis') || txt.includes('lhos')) {
          handing = (label ? label.textContent : inp.value).replace(/\s*\(\+.*?\)/, '').trim();
        }
      });

      return { doorConfig: doorConfig, handing: handing };
    }

    function update() {
      const userQty = Math.max(1, parseInt(qtyInput ? qtyInput.value : 1, 10) || 1);
      const ymqAddon = getYmqAddonCents();
      const isDouble = isDoubleDoorSelected();

      // Single door price = base variant price + any prehung addon fee
      const singleDoorPrice = basePrice + ymqAddon;

      // Double Door pair = 2 x single prehung door price, and 2x cart quantity
      const multiplier = isDouble ? 2 : 1;
      const effectiveUnitPrice = singleDoorPrice * multiplier;
      const totalCents = userQty * effectiveUnitPrice;
      const formQty = userQty * multiplier;

      if (unitPriceEl) unitPriceEl.textContent = formatMoney(effectiveUnitPrice);
      if (totalEl) totalEl.textContent = formatMoney(totalCents);

      setProp(hiddenQty, formQty);

      // Sync dynamic Door Configuration and Handing into form properties
      const doorOpts = getDoorSelectedOptions();
      const propDoorType = wrapper.querySelector('[data-prop-door-type]');
      const propHanding = wrapper.querySelector('[data-prop-handing]');
      const configSummary = wrapper.querySelector('[data-calc-config]');

      if (propDoorType) {
        setProp(propDoorType, doorOpts.doorConfig);
        propDoorType.removeAttribute('disabled');
      }
      if (propHanding) {
        setProp(propHanding, doorOpts.handing);
        propHanding.removeAttribute('disabled');
      }
      if (configSummary) {
        configSummary.textContent = doorOpts.doorConfig + (doorOpts.handing !== 'N/A' ? ' - ' + doorOpts.handing : '');
      }

      setButtonState(wrapper, true, 'ADD TO CART', totalCents, null);
    }

    function syncVariant(explicitVariant) {
      let newPrice = null;
      if (explicitVariant && explicitVariant.price) {
        newPrice = explicitVariant.price;
      } else {
        const selection = getCurrentVariantSelection(wrapper);
        if (selection && selection.variant && selection.variant.price) {
          newPrice = selection.variant.price;
        }
      }
      if (newPrice !== null && newPrice !== basePrice) {
        basePrice = newPrice;
        update();
      }
    }

    if (qtyInput) qtyInput.addEventListener('input', update);

    if (minusBtn && !minusBtn._calcBound) {
      minusBtn._calcBound = true;
      minusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (qtyInput) {
          qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
          update();
        }
      });
    }
    if (plusBtn && !plusBtn._calcBound) {
      plusBtn._calcBound = true;
      plusBtn.addEventListener('click', function (e) {
        e.preventDefault();
        e.stopPropagation();
        if (qtyInput) {
          qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
          update();
        }
      });
    }

    // Safely listen for Ymq option changes (user change/click events only — zero infinite loops)
    document.addEventListener('change', function (e) {
      if (e.target && e.target.closest('.ymq-options, [class*="ymq"], form[action*="/cart/add"]')) {
        update();
      }
    });

    document.addEventListener('click', function (e) {
      if (e.target && e.target.closest('.ymq-options, [class*="ymq"]')) {
        setTimeout(update, 50);
      }
    });

    registerVariantListener(syncVariant);
    update();
    setTimeout(update, 400);
    setTimeout(update, 1200);
  }

  /* ─────────────────────────────────────────
     PREVENT CART SUBMIT WITHOUT CALCULATOR INPUT
  ───────────────────────────────────────── */

  function guardCartSubmit(wrapper, formId) {
    const form = document.getElementById(formId) ||
      document.querySelector('[data-type="add-to-cart-form"]');

    if (!form || form._calcStockGuardBound) return;

    form._calcStockGuardBound = true;
    stockWrappers.add(wrapper);

    let replayingSubmit = false;
    let checkingSubmit = false;

    function blockSubmit(event, message) {
      event.preventDefault();
      event.stopImmediatePropagation();
      showCalcToast(message);
    }

    form.addEventListener('submit', function (event) {
      const qtyInput =
        wrapper.querySelector('input[name="quantity"]') ||
        wrapper.querySelector('[data-calc-qty]');

      const qty = Number(qtyInput ? qtyInput.value : 0);
      const button = getSubmitButton(wrapper);

      // Validate the calculator before allowing any submission.
      if (
        !Number.isInteger(qty) ||
        qty < 1 ||
        (button &&
          button.getAttribute('data-calc-ready') === 'false')
      ) {
        blockSubmit(
          event,
          'Please complete the calculator before adding to cart.'
        );
        return;
      }

      // Copy calculator quantity and properties into the form.
      const calcInputs = wrapper.querySelectorAll(
        'input[name="quantity"], input[name^="properties"]'
      );

      calcInputs.forEach(function (input) {
        if (
          input.value === undefined ||
          input.value === null ||
          input.value === ''
        ) return;

        let formInput = form.querySelector(
          'input[name="' + CSS.escape(input.name) + '"]'
        );

        if (!formInput) {
          formInput = document.createElement('input');
          formInput.type = 'hidden';
          formInput.name = input.name;
          form.appendChild(formInput);
        }

        formInput.value = input.value;
      });

      // Require an exact selected variant.
      const selection = getCurrentVariantSelection(wrapper);
      const idInput = form.querySelector('input[name="id"]');

      if (!selection.variant || !idInput) {
        blockSubmit(event, 'Please select a valid product option.');
        return;
      }

      idInput.value = String(selection.variant.id);
      const variantId = idInput.value;

      const nativeQty = document.getElementById(
        'Quantity-' + wrapper.dataset.sectionId
      );

      if (nativeQty) nativeQty.value = String(qty);

      const inventory = readCalcInventory(wrapper);
      const stock = inventory && inventory[variantId];

      if (!stock) {
        blockSubmit(
          event,
          'Inventory data is missing. Please refresh the page.'
        );
        return;
      }

      // Untracked variants keep the normal submission path.
      if (!stock.tracked) return;

      // Replayed submission still checks the current quantity.
      if (replayingSubmit) {
        const state = getCalcStockState(wrapper, variantId);

        if (state.blocked) {
          blockSubmit(event, state.message);
          applyCalcStockState(wrapper);
        }

        return;
      }

      // Stop the theme's submit handler while checking the cart.
      event.preventDefault();
      event.stopImmediatePropagation();

      if (checkingSubmit) return;
      checkingSubmit = true;

      const submitter = event.submitter;

      refreshCalcCart()
        .then(function () {
          if (!wrapper.isConnected || !form.isConnected) return;

          const currentSelection =
            getCurrentVariantSelection(wrapper);

          const currentQty =
            Number(qtyInput ? qtyInput.value : 0);

          if (
            !currentSelection.variant ||
            String(currentSelection.variant.id) !== variantId ||
            currentQty !== qty
          ) {
            showCalcToast(
              'Your selection or quantity changed. Please add it again.'
            );
            return;
          }

          const state = getCalcStockState(wrapper, variantId);

          if (state.blocked) {
            applyCalcStockState(wrapper);
            showCalcToast(state.message);
            return;
          }

          // Let the existing theme perform the actual add-to-cart.
          replayingSubmit = true;

          try {
            if (submitter && submitter.form === form) {
              form.requestSubmit(submitter);
            } else {
              form.requestSubmit();
            }
          } finally {
            replayingSubmit = false;
          }
        })
        .catch(function (error) {
          console.error('Calculator stock check failed:', error);
          showCalcToast(
            'Could not check availability. Please try again.'
          );
        })
        .finally(function () {
          checkingSubmit = false;
        });
    }, true);

    // Load cart quantities when the guard is initialized,
    // rather than waiting for the first submission.
    refreshCalcCart().catch(function () {
      showCalcToast(
        'Could not check your cart. Please refresh the page.'
      );
    });
  }


  function showCalcToast(msg) {
    let toast = document.getElementById('calc-toast');
    if (!toast) {
      toast = document.createElement('div');
      toast.id = 'calc-toast';
      toast.className = 'calc-toast';
      document.body.appendChild(toast);
    }
    toast.textContent = msg;
    toast.classList.add('calc-toast--visible');
    setTimeout(function () {
      toast.classList.remove('calc-toast--visible');
    }, 3500);
  }

  /* ─────────────────────────────────────────
     INITIALISE ALL CALCULATORS ON PAGE
  ───────────────────────────────────────── */

  function init() {
    const wrappers = document.querySelectorAll('.collection-calculator');

    wrappers.forEach(function (wrapper) {
      if (wrapper._calcInitialized) return;
      wrapper._calcInitialized = true;

      const type = wrapper.dataset.calculatorType;
      const sectionId = wrapper.dataset.sectionId;
      const formId = 'product-form-' + sectionId;

      switch (type) {
        case 'each':
          initCalcEach(wrapper);
          guardCartSubmit(wrapper, formId);
          break;
        case 'box_based':
          initCalcBoxBased(wrapper);
          guardCartSubmit(wrapper, formId);
          break;
        case 'moulding':
        case 'linear_piece':
          initCalcMoulding(wrapper);
          guardCartSubmit(wrapper, formId);
          break;
        case 'vinyl_sheet':
          initCalcVinylSheet(wrapper);
          guardCartSubmit(wrapper, formId);
          break;
        case 'door':
          initCalcDoor(wrapper);
          guardCartSubmit(wrapper, formId);
          break;
        default:
          break;
      }
    });
  }

  // Support Shopify Theme Editor section rendering
  document.addEventListener('shopify:section:load', function (e) {
    if (e.target) {
      const wrappers = e.target.querySelectorAll('.collection-calculator');
      wrappers.forEach(function (w) {
        w._calcInitialized = false;
      });
    }
    init();
  });

  document.addEventListener('shopify:section:select', function (e) {
    if (e.target) {
      const wrappers = e.target.querySelectorAll('.collection-calculator');
      wrappers.forEach(function (w) {
        w._calcInitialized = false;
      });
    }
    init();
  });

  window._collectionCalculatorInit = init;

  // Run on DOM ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

})();
